import { fastEqual } from '@flowus/common/utils/tools';
import { BehaviorSubject } from 'rxjs';
import type { ObservableDataValueType } from './map';
import { globalRxTimer } from './timer';

export class ObservableObject {
  private observableMap: Map<string, BehaviorSubject<ObservableDataValueType>>;
  private patchQueue: Map<string, () => void>;

  constructor(observableMap = new Map<string, BehaviorSubject<ObservableDataValueType>>()) {
    this.observableMap = observableMap;
    this.patchQueue = new Map<string, () => void>();
    this.runPatchQueue = () => {
      for (const [key, patch] of this.patchQueue) {
        this.patchQueue.delete(key);
        patch();
      }
    };
  }

  private runPatchQueue: () => void;

  patch(id: string, value?: ObservableDataValueType) {
    const newValue = value ?? (typeof id === 'symbol' ? id : Symbol(id));
    if (this.observableMap.has(id)) {
      const obs = this.observableMap.get(id);
      if (obs) {
        const oldValue = obs.getValue();
        // 对比新旧值是否有变化，无变化不执行 patch，但此处可能有 BUG，建议不要轻易传递 value，让他自然 patch
        const check = fastEqual(oldValue, newValue);
        if (!check) {
          // 如果是重置选中状态的情况，需要马上派发
          if (isSelectPrefixId(id) && newValue === resetSelectValue) {
            obs.next(newValue);
          } else {
            this.patchQueue.set(id, () => obs.next(newValue));
            this.runPatchQueue();
          }
        }
        return obs;
      }
    }

    const subject = new BehaviorSubject<ObservableDataValueType>(newValue);
    this.observableMap.set(id, subject);
    globalRxTimer.start();
    this.runPatchQueue();
    return subject;
  }

  deleteObs(id: string) {
    const obs = this.observableMap.get(id);
    if (obs) {
      obs.unsubscribe();
      this.observableMap.delete(id);
    }
  }

  get(id: string) {
    const obs = this.observableMap.get(id);
    if (obs) {
      return obs;
    }

    // 如果要监听框选的时候，就得开始建立 obs 联系
    if (isSelectPrefixId(id)) {
      return this.patch(id, resetSelectValue);
    }

    return this.patch(id);
  }

  clear() {
    this.observableMap.forEach((obs) => obs.unsubscribe());
    this.observableMap.clear();
  }

  getMap() {
    return this.observableMap;
  }
}

// #region select
export const prefixSelectBlock = 'select=block';
export const prefixSelectCell = 'select=cell';
export const resetSelectValue = false;
export const isSelectPrefixId = (id = '') => {
  if (typeof id === 'string') {
    return id?.startsWith?.(prefixSelectBlock) || id?.startsWith?.(prefixSelectCell);
  }
  return false;
};

// #region selectBlock
export const getSelectedBlocksKey = (item: {
  blockId?: string;
  syncId?: string;
  viewId?: string;
}) => {
  if (item.viewId && item.blockId && item.syncId) {
    return `${prefixSelectBlock}&blockId=${item.blockId}&syncId=${item.syncId}&viewId=${item.viewId}`;
  }
  if (item.blockId) {
    return getSelectedBlocksKeyByBlock(item.blockId);
  }
  if (item.syncId) {
    return getSelectedBlocksKeyBySync(item.syncId);
  }
  if (item.viewId) {
    return getSelectedBlocksKeyByView(item.viewId);
  }
  return `${prefixSelectBlock}`;
};

export const getSelectedBlocksKeyBySync = (syncId: string) => {
  return `${prefixSelectBlock}&syncId=${syncId}`;
};

export const getSelectedBlocksKeyByBlock = (blockId: string) => {
  return `${prefixSelectBlock}&blockId=${blockId}`;
};

export const getSelectedBlocksKeyByView = (viewId: string) => {
  return `${prefixSelectBlock}&viewId=${viewId}`;
};
// #endregion

// #region selectCell
export const getSelectedCellKey = (item: {
  recordId?: string;
  viewId?: string;
  propertyId?: string;
}) => {
  if (item.propertyId && item.recordId && item.viewId) {
    return `${prefixSelectCell}&recordId=${item.recordId}&viewId=${item.viewId}&propertyId=${item.propertyId}`;
  }

  if (item.recordId) {
    return `${prefixSelectCell}&recordId=${item.recordId}`;
  }

  if (item.viewId) {
    return `${prefixSelectCell}&viewId=${item.viewId}`;
  }

  if (item.propertyId) {
    return `${prefixSelectCell}&propertyId=${item.propertyId}`;
  }

  return `${prefixSelectCell}`;
};
// #endregion

// #endregion

// #region simpleTable
export const prefixSimpleTable = 'simpleTable';
export const isSimpleTableKey = (id = '') => {
  if (typeof id === 'string') {
    return id.startsWith(prefixSimpleTable);
  }
  return false;
};
// #endregion
